import React, { useEffect, useRef, useState } from "react";
import DOMPurify from "dompurify";
import { Code, TextB, TextItalic, TextStrikethrough, ListBullets, ListNumbers, LinkSimple, Minus, TextT } from "@phosphor-icons/react";

const Btn = ({ onClick, title, children }) => (
  <button type="button" title={title} onMouseDown={(e) => e.preventDefault()} onClick={onClick}
    className="flex h-8 w-8 items-center justify-center text-foreground hover:bg-muted transition-colors duration-150">
    {children}
  </button>
);

const EDITOR_CSS = `
.rte-area ul{list-style:disc;padding-left:1.5rem;margin:0.5rem 0}
.rte-area ol{list-style:decimal;padding-left:1.5rem;margin:0.5rem 0}
.rte-area a{color:#2563eb;text-decoration:underline}
.rte-area p{margin:0.5rem 0}
.rte-area:empty:before{content:attr(data-placeholder);color:#9ca3af}
`;

export const RichTextEditor = ({ value, onChange, testid, placeholder }) => {
  const ref = useRef(null);
  const last = useRef("");
  const [source, setSource] = useState(false);

  useEffect(() => {
    if (!source && ref.current && value !== last.current) {
      ref.current.innerHTML = DOMPurify.sanitize(value || "");
      last.current = value || "";
    }
  }, [value, source]);

  const emit = () => { const html = ref.current.innerHTML; last.current = html; onChange(html); };
  const exec = (cmd, arg = null) => { ref.current && ref.current.focus(); document.execCommand(cmd, false, arg); emit(); };
  const link = () => { const url = window.prompt("URL (https://...):"); if (url) exec("createLink", url); };

  return (
    <div className="border border-border bg-background" data-testid={testid}>
      <style>{EDITOR_CSS}</style>
      <div className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/40 px-1 py-1">
        <Btn title="Source HTML" onClick={() => setSource((s) => !s)}><Code size={16} weight="bold" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn title="Paragraphe" onClick={() => exec("formatBlock", "P")}><TextT size={16} weight="bold" /></Btn>
        <Btn title="Gras" onClick={() => exec("bold")}><TextB size={16} weight="bold" /></Btn>
        <Btn title="Italique" onClick={() => exec("italic")}><TextItalic size={16} weight="bold" /></Btn>
        <Btn title="Barré" onClick={() => exec("strikeThrough")}><TextStrikethrough size={16} weight="bold" /></Btn>
        <span className="mx-1 h-5 w-px bg-border" />
        <Btn title="Liste à puces" onClick={() => exec("insertUnorderedList")}><ListBullets size={16} weight="bold" /></Btn>
        <Btn title="Liste numérotée" onClick={() => exec("insertOrderedList")}><ListNumbers size={16} weight="bold" /></Btn>
        <Btn title="Lien" onClick={link}><LinkSimple size={16} weight="bold" /></Btn>
        <Btn title="Séparateur" onClick={() => exec("insertHorizontalRule")}><Minus size={16} weight="bold" /></Btn>
      </div>
      {source ? (
        <textarea data-testid={testid ? `${testid}-source` : undefined} value={value || ""} rows={12}
          onChange={(e) => { last.current = e.target.value; onChange(e.target.value); }}
          className="w-full bg-background px-3 py-2 text-xs font-mono outline-none" />
      ) : (
        <div ref={ref} contentEditable suppressContentEditableWarning
          data-testid={testid ? `${testid}-area` : undefined} data-placeholder={placeholder || ""}
          onInput={emit}
          className="rte-area min-h-[220px] max-h-[440px] overflow-y-auto bg-background px-3 py-2 text-sm leading-relaxed outline-none" />
      )}
    </div>
  );
};
